

import { startQuiz, listQuizTopics, startMocKExamSession, getQuizQuestions, getQuizHistory, getSessionResult, getChapterReview, submitAnswer, quickQuiz, loadModuleAssessment, getOpenQuizSessions, resumeQuizSession } from '../thunks/quizThunk'
import { createSlice } from '@reduxjs/toolkit'

const initialState = {
    quiz: null,
    topics: [],
    quizQuestions: null,
    quizHistory: [],
    openSessions: [],
    sessionResult: null,
    submittedAnswer: null,
    loading: false,
    error: null,
}

// A failed request sometimes gets back a raw HTML error page (e.g. a
// framework's default 404/500 page) instead of JSON — axios hands that back
// as a plain string, which would otherwise get rendered verbatim as if it
// were a real error message. Treat anything that looks like markup as an
// unknown error instead of displaying it.
const looksLikeHtml = (value) => typeof value === 'string' && /<\/?[a-z][\s\S]*>/i.test(value.trim())

const getErrorMessage = (payload, fallback) => {
    if (typeof payload === 'string') return looksLikeHtml(payload) ? (fallback || 'Request failed.') : payload
    return payload?.error || payload?.detail || payload?.message || fallback || 'Request failed.'
}

const normalizeHistory = (payload) => {
    if (Array.isArray(payload)) return payload
    return payload?.results || payload?.history || payload?.sessions || payload?.data || []
}

const quizSlice = createSlice({
    name: 'quiz',
    initialState,
    reducers: {
        resetQuiz: (state) => {
            state.quiz = null
            state.quizQuestions = null
            state.sessionResult = null
            state.submittedAnswer = null
            state.loading = false
            state.error = null
        },
        setNextQuestion: (state) => {
            if (state.submittedAnswer?.next_question) {
                const nextQuestion = state.submittedAnswer.next_question
                state.quizQuestions = {
                    question_number: nextQuestion.question_number || (state.quizQuestions?.question_number || 1) + 1,
                    total_questions: state.submittedAnswer.total_questions || state.quizQuestions?.total_questions || state.quiz?.total_questions,
                    question: nextQuestion,
                    already_answered: false,
                }
                state.submittedAnswer = null
                state.error = null
            }
        },
        setMockExamTimeRemaining: (state, action) => {
            if (state.quiz) {
                state.quiz.time_remaining_seconds = Math.max(0, action.payload || 0)
            }
        },
        clearQuizError: (state) => {
            state.error = null
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(startQuiz.pending, (state) => {
                state.loading = true
                state.error = null
            }).addCase(startQuiz.fulfilled, (state, action) => {
                state.loading = false
                state.quiz = action.payload
                state.quizQuestions = action.payload?.first_question
                    ? {
                        question_number: 1,
                        total_questions: action.payload?.total_questions,
                        question: action.payload.first_question,
                    }
                    : state.quizQuestions
                state.sessionResult = null
                state.submittedAnswer = null
            }).addCase(startQuiz.rejected, (state, action) => {
                state.loading = false
                state.error = getErrorMessage(action.payload, action.error.message)
            }
            ).addCase(listQuizTopics.pending, (state) => {
                state.loading = true
                state.error = null
            }).addCase(listQuizTopics.fulfilled, (state, action) => {
                state.loading = false
                state.topics = action.payload
            }).addCase(listQuizTopics.rejected, (state, action) => {
                state.loading = false
                state.error = getErrorMessage(action.payload, action.error.message)
            }).addCase(getQuizQuestions.pending, (state) => {
                state.loading = true
                state.error = null
            }).addCase(getQuizQuestions.fulfilled, (state, action) => {
                state.loading = false
                state.quizQuestions = action.payload
                state.submittedAnswer = null
            }).addCase(getQuizQuestions.rejected, (state, action) => {
                state.loading = false
                state.error = getErrorMessage(action.payload, action.error.message)
            }).addCase(getQuizHistory.pending, (state) => {
                state.loading = true
                state.error = null
            }).addCase(getQuizHistory.fulfilled, (state, action) => {
                state.loading = false
                state.quizHistory = normalizeHistory(action.payload)
            }).addCase(getQuizHistory.rejected, (state, action) => {
                state.loading = false
                state.error = getErrorMessage(action.payload, action.error.message)
            }).addCase(getOpenQuizSessions.fulfilled, (state, action) => {
                state.openSessions = action.payload?.sessions || []
            }).addCase(resumeQuizSession.pending, (state) => {
                state.loading = true
                state.error = null
            }).addCase(resumeQuizSession.fulfilled, (state, action) => {
                state.loading = false
                state.quiz = action.payload
                state.quizQuestions = {
                    question_number: action.payload.resume_question_number || 1,
                    total_questions: action.payload.total_questions,
                    question: action.payload.first_question,
                }
                state.sessionResult = null
                state.submittedAnswer = null
            }).addCase(resumeQuizSession.rejected, (state, action) => {
                state.loading = false
                state.error = getErrorMessage(action.payload, action.error.message)
            }).addCase(getChapterReview.pending, (state) => {
                state.loading = true
                state.error = null
            }).addCase(getChapterReview.fulfilled, (state, action) => {
                state.loading = false
                state.sessionResult = action.payload
            }).addCase(getChapterReview.rejected, (state, action) => {
                state.loading = false
                state.error = getErrorMessage(action.payload, action.error.message)
            }).addCase(getSessionResult.pending, (state) => {
                state.loading = true
                state.error = null
            }).addCase(getSessionResult.fulfilled, (state, action) => {
                state.loading = false
                state.sessionResult = action.payload
            }).addCase(getSessionResult.rejected, (state, action) => {
                state.loading = false
                state.error = getErrorMessage(action.payload, action.error.message)
            }).addCase(submitAnswer.pending, (state) => {
                state.loading = true
                state.error = null
            }).addCase(submitAnswer.fulfilled, (state, action) => {
                state.loading = false
                state.submittedAnswer = action.payload
            }).addCase(submitAnswer.rejected, (state, action) => {
                state.loading = false
                state.error = getErrorMessage(action.payload, action.error.message)
            }).addCase(startMocKExamSession.pending, (state) => {
                state.loading = true
                state.error = null
            }).addCase(startMocKExamSession.fulfilled, (state, action) => {
                state.loading = false;

                state.quiz = action.payload;

                state.quizQuestions = {
                    question_number: action.payload.resume_question_number || 1,
                    total_questions: action.payload.total_questions,
                    question: action.payload.first_question,
                };

                state.sessionResult = null;
                state.submittedAnswer = null;
            }).addCase(startMocKExamSession.rejected, (state, action) => {
                state.loading = false
                state.error = getErrorMessage(action.payload, action.error.message)
            }).addCase(quickQuiz.pending, (state) => {
                state.loading = true
                state.error = null
                state.quiz = null
            }).addCase(quickQuiz.fulfilled, (state, action) => {
                state.loading = false
                state.quiz = action.payload
            }).addCase(quickQuiz.rejected, (state, action) => {
                state.loading = false
                state.error = getErrorMessage(action.payload, action.error.message)
            }).addCase(loadModuleAssessment.pending, (state) => {
                state.loading = true
                state.error = null
                state.quiz = null
            }).addCase(loadModuleAssessment.fulfilled, (state, action) => {
                state.loading = false
                state.quiz = action.payload
            }).addCase(loadModuleAssessment.rejected, (state, action) => {
                state.loading = false
                state.error = getErrorMessage(action.payload, action.error.message)
            })
    }
})


export const { resetQuiz, setNextQuestion, setMockExamTimeRemaining, clearQuizError } = quizSlice.actions
export default quizSlice.reducer
